<template>
    <div class="action-button-updater">
        <div class="tabs">
            <div class="tab-button" :class="{ active: currentTab === 0 }" @click="currentTab = 0">
                <span class="material-icons"> tune </span>
                <span class="title">General</span>
            </div>
            <div class="tab-button" :class="{ active: currentTab === 1 }" @click="currentTab = 1" v-if="!whatsappMode">
                <span class="material-icons"> bolt </span>
                <span class="title">Actions</span>
            </div>
        </div>
        <div class="general-tab" :class="{ 'd-none': currentTab !== 0 }">
            <div class="section">
                <div class="title">{{ $t("message.buttonTitle") }}</div>
                <TiptapEditor
                    placeholder="Button title"
                    :show-attributes="showAttributes"
                    :show-emoji="showEmoji"
                    :max-character-count="20"
                    v-model="clone.title"
                    :whatsapp-mode="whatsappMode"
                    :allowed-attributes="allowedAttributes"
                />
            </div>
            <div class="section">
                <div class="title">{{ $t("message.buttonType") }}</div>
                <a-select class="w-100" @change="handleButtonTypeChange" :value="clone.type">
                    <a-select-option
                        :value="buttonType.type"
                        v-for="buttonType of getAllowedButtonTypes()"
                        :key="buttonType.type"
                    >
                        {{ buttonType.title }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="section" v-if="['OPEN_MOMENT', 'OPEN_URL'].includes(clone.type)" :key="clone.type">
                <div class="title">Website Address</div>
                <TiptapEditor
                    placeholder="Enter URL"
                    :show-attributes="true"
                    :show-emoji="false"
                    :max-character-count="2048"
                    v-model="clone.payload.url"
                    :whatsapp-mode="whatsappMode"
                    :allowed-attributes="allowedAttributes"
                />
            </div>
            <div class="section" v-if="clone.type === 'PHONE_CALL'" :key="clone.type">
                <div class="title">Phone Number</div>
                <TiptapEditor
                    placeholder="Enter Phone Number"
                    :show-attributes="true"
                    :show-emoji="false"
                    :max-character-count="16"
                    v-model="clone.payload.phoneNumber"
                    :whatsapp-mode="whatsappMode"
                    :allowed-attributes="allowedAttributes"
                />
            </div>
            <div class="section" v-if="clone.type === 'OPEN_MOMENT'" :key="clone.type">
                <div class="title">Window Size</div>
                <a-select
                    class="w-100"
                    @change="(type) => (clone.payload.windowSize = type)"
                    :value="clone.payload.windowSize"
                >
                    <a-select-option :value="size.type" v-for="size of WindowSizes" :key="size.type">
                        {{ size.title }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="section" v-if="clone.type === 'GO_TO_BLOCK'" :key="clone.type">
                <div class="title">Go to</div>
                <BlockPicker :flattedDiagram="flattedDiagram" v-model="clone.payload.blockId" />
            </div>
        </div>
        <div class="action-tab" :class="{ 'd-none': currentTab !== 1 }">
            <div
                class="single-action"
                :class="{ active: setAttributeActionActive }"
                v-if="getSetAttributesActions().length"
            >
                <div class="remove-action">
                    <md-button class="md-icon-button md-mini" @click="removeAll">
                        <md-icon>delete_outline</md-icon>
                    </md-button>
                </div>
                <div class="single-action-title" @click="setAttributeActionActive = !setAttributeActionActive">
                    <span class="material-icons text-primary"> data_object </span>
                    <div>Set custom attribute</div>
                </div>
                <div class="single-action-content">
                    <div class="set-attributes">
                        <div class="attributes-set">
                            <AttributeSetUpdater
                                v-for="(action, i) in getSetAttributesActions()"
                                v-model="clone.actions[findActionIndex(action.id)].payload"
                                :show-remove-button="true"
                                :on-removed="() => removeAction(action.id)"
                                :key="action.id"
                            />
                            <div class="attributes-set-add">
                                <div class="attributes-set-add-wrapper">
                                    <div class="attributes-set-add-line"></div>
                                    <div class="attributes-set-add-button" @click="addSetCustomAttributeAction">
                                        <div class="attributes-set-add-plus">
                                            <span class="material-icons"> add </span>
                                        </div>
                                        <div class="attributes-set-add-text">Add attribute</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="add-single-action" v-if="!clone.actions.length" @click="addSetCustomAttributeAction">
                <span class="material-icons"> add </span>
                <span> Add new action </span>
            </div>
        </div>
        <div class="footer">
            <md-button class="md-raised md-primary d-block w-100" :disabled="!saveEnabled()" @click="save"
                >Save Settings
            </md-button>
        </div>
    </div>
</template>

<script>
import TiptapEditor from "./TiptapEditor";
import log2 from "../../../../../../utils/log2";
import _ from "lodash";
import AttributeSetUpdater from "./AttributeSetUpdater";
import { generateRandomId } from "../../../../../../utils/session";
import BlockPicker from "../pickers/BlockPicker";

const log = log2("ActionButtonUpdater");

export default {
    name: "ActionButtonUpdater",
    components: {
        TiptapEditor,
        AttributeSetUpdater,
        BlockPicker,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: {
                id: "",
                title: "Button",
                type: "SEND_MESSAGE",
                payload: {},
                actions: [], //{type: "SET_ATTRIBUTE", payload: {attribute:"custom",value:1} },
            },
        },
        onCompleted: {
            type: Function,
            required: true,
        },
        popupVisible: {
            type: Boolean,
            required: true,
        },
        allowedMessageTypes: {
            type: Array,
            required: false,
            default: () => [],
        },
        flattedDiagram: {
            type: Array,
            required: true,
            default: () => [],
        },
        allowedAttributes: {
            type: Array,
            default: () => [],
        },
        whatsappMode: {
            type: Boolean,
            default: false,
        },
        previewMode: {
            type: Boolean,
            default: false,
        },
        showAttributes: {
            type: Boolean,
            default: true,
        },
        showEmoji: {
            type: Boolean,
            default: true,
        },
        showEmojiPicker: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            setAttributeActionActive: false,
            currentTab: 0,
            whatsappAllowedButtonTypes: ["SEND_MESSAGE", "OPEN_URL", "PHONE_CALL"],
            ButtonTypes: [
                {
                    type: "SEND_MESSAGE",
                    title: this.$t("message.buttonSendMessage"),
                    payload: {},
                },
                {
                    type: "GO_TO_BLOCK",
                    title: this.$t("message.buttonGoToBlock"),
                    payload: {
                        blockId: null,
                    },
                },
                {
                    type: "OPEN_URL",
                    title: this.$t("message.buttonOpenUrl"),
                    payload: {
                        url: null,
                    },
                },
                {
                    type: "PHONE_CALL",
                    title: this.$t("message.buttonPhoneCall"),
                    payload: {
                        phoneNumber: "+",
                    },
                },
                {
                    type: "OPEN_MOMENT",
                    title: this.$t("message.buttonOpenMoment"),
                    payload: {
                        url: null,
                        windowSize: "full",
                    },
                },
                {
                    type: "CLOSE_CHAT",
                    title: this.$t("message.buttonCloseChat"),
                    payload: {},
                },
                {
                    type: "TRANSFER_CHAT",
                    title: this.$t("message.buttonTransferChat"),
                    payload: {},
                },
                {
                    type: "CREATE_TICKET",
                    title: this.$t("message.buttonCreateTicket"),
                    payload: {
                        message: null,
                        tag: null,
                    },
                },
            ],
            WindowSizes: [
                {
                    type: "full",
                    title: "Full - 100%",
                },
                {
                    type: "tall",
                    title: "Tall - 75%",
                },
                {
                    type: "compact",
                    title: "Compact - 50%",
                },
            ],
            clone: _.cloneDeep(this.value),
        };
    },
    methods: {
        removeAction(id) {
            log("removeAction", id, [...this.clone.actions]);
            this.clone.actions = this.clone.actions.filter((action) => action.id !== id);
        },
        removeAll() {
            log("removeAll");
            this.clone.actions = [];
        },
        getSetAttributesActions() {
            return this.clone.actions.filter((action) => action.type === "SET_ATTRIBUTE");
        },
        findActionIndex(id) {
            return (this.clone.actions || []).findIndex((action) => action.id === id);
        },
        getAllowedButtonTypes() {
            if (this.whatsappMode) {
                return this.ButtonTypes.filter((type) => this.whatsappAllowedButtonTypes.includes(type.type));
            } else {
                return this.allowedMessageTypes.length
                    ? this.ButtonTypes.filter((buttonType) => this.allowedMessageTypes.includes(buttonType.type))
                    : this.ButtonTypes;
            }
        },
        handleButtonTypeChange(type) {
            const button = this.ButtonTypes.find((x) => x.type === type);
            log("handleButtonTypeChange ->", type, button);
            if (button) {
                this.clone.type = button.type;
                this.clone.payload = button.payload;
                this.clone.actions = [];
            }
        },
        save() {
            log("save ->", { old: this.value, new: this.clone });
            this.$emit("input", this.clone);
            this.onCompleted();
        },
        saveEnabled() {
            return !_.isEqual(this.value, this.clone);
        },
        addSetCustomAttributeAction() {
            this.clone.actions.push({
                id: generateRandomId(),
                type: "SET_ATTRIBUTE",
                payload: {
                    attribute: "",
                    value: "",
                },
            });
            this.setAttributeActionActive = true;
        },
    },
    mounted() {
        log("mounted", this.value);
    },
    beforeDestroy() {
        log("beforeDestroy");
    },
    watch: {
        clone: {
            handler(val) {
                log("watch clone ->", val);
            },
            deep: true,
        },
        value: {
            handler(val) {
                log("watch value ->", val);
                this.clone = _.cloneDeep(this.value);
            },
            deep: true,
        },
        popupVisible: {
            handler(val) {
                log("watch value ->", val);
                this.clone = _.cloneDeep(this.value);
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.action-button-updater {
    max-width: 90vw;
    width: 380px;
    display: flex;
    flex-direction: column;

    .editable-editor-wrapper {
        height: 35px;
    }

    .tabs {
        align-items: center;
        border-bottom: 1px solid #c8d0d8;
        display: flex;
        height: 55px;
        padding: 0 25px;

        .tab-button {
            align-items: center;
            cursor: pointer;
            display: flex;
            margin-right: 25px;
            transition: all 0.3s;

            &.active {
                color: #008aff;

                .material-icons {
                    color: #008aff;
                }
            }

            .title {
                font-size: 18px;
                line-height: 23px;
                font-weight: 600;
            }
        }
    }

    .general-tab {
        padding: 25px;
        text-align: left;

        .section {
            color: #516173;
            margin-bottom: 15px;
        }
    }

    .action-tab {
        max-height: calc(60vh - 150px);
        overflow-y: auto;
        padding: 25px;
        text-align: left;

        .add-single-action {
            align-items: center;
            border: 1px dashed #006cff;
            border-radius: 4px;
            color: #006cff;
            cursor: pointer;
            display: flex;
            height: 44px;
            justify-content: center;
            transition: all 0.3s;
            user-select: none;
            width: 100%;

            span {
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }
        }

        .single-action {
            background: #e8eef3;
            border: 1px solid #e8eef3;
            border-radius: 4px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-bottom: 16px;
            padding: 12px 20px;
            position: relative;
            width: 100%;

            .single-attribute-wrapper {
                padding: 0px !important;
                margin-top: 20px !important;
            }

            .single-attribute {
                background: #e8eef3;
                padding: 10px;
            }

            .single-action-content {
                display: none;
            }

            &.active {
                background: transparent;
                border-color: #c8d0d8;

                .single-action-content {
                    display: flex;
                    flex-direction: column;
                }
            }

            &:hover {
                .remove-action {
                    opacity: 1;
                }
            }

            .remove-action {
                align-items: center;
                background: #fff;
                border-radius: 100%;
                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                cursor: pointer;
                display: flex;
                height: 26px;
                justify-content: center;
                left: -10px;
                opacity: 0;
                position: absolute;
                top: -10px;
                transition: all 0.3s;
                width: 26px;

                .md-button {
                    background-color: #fff;
                    align-items: center;
                    border-radius: 100%;
                    box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                    cursor: pointer;
                    display: flex;
                    justify-content: center;
                    margin-bottom: 7px;
                    transition: background-color 0.3s;
                    height: 30px;
                    width: 30px;
                    min-height: 30px !important;
                    min-width: 30px !important;

                    .md-icon {
                        color: #006cff;
                    }
                }
            }

            .single-action-title {
                align-items: center;
                display: flex;
                cursor: pointer;

                div {
                    margin-left: 5px;
                    font-weight: 600;
                    font-size: 16px;
                    line-height: 20px;
                }
            }

            .single-action-content {
                margin-top: 24px;

                .attributes-set-add {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 100%;

                    .attributes-set-add-wrapper {
                        display: flex;
                        flex-direction: column;

                        .attributes-set-add-line {
                            border-left: 1px dashed #b4c0c7;
                            border-right: 1px dashed #b4c0c7;
                            height: 28px;
                            margin-left: 10px;
                            width: 0;
                        }

                        .attributes-set-add-button {
                            align-items: center;
                            cursor: pointer;
                            display: flex;

                            .attributes-set-add-plus {
                                align-items: center;
                                background: #fff;
                                border-radius: 100%;
                                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                                display: flex;
                                height: 22px;
                                justify-content: center;
                                transition: background-color 0.6s;
                                width: 22px;

                                &:hover {
                                    background-color: #006cff;
                                    color: white;
                                }
                            }

                            .attributes-set-add-text {
                                color: #475e7a;
                                font-size: 14px;
                                font-weight: 600;
                                line-height: 18px;
                                margin-left: 8px;
                                white-space: nowrap;
                            }
                        }
                    }
                }
            }
        }
    }

    .footer {
        background: #f0f4f7;
        border-radius: 0 0 4px 4px;
        border-top: 1px solid #c8d0d8;
        padding: 20px 25px;
        -webkit-transition: background 0.3s;
        transition: background 0.3s;
    }

    .editable-editor {
        border: 1px solid #c6cdd4 !important;
    }
}
</style>
